<script lang="ts">
  export * from './IntegrationIcon.types';
</script>

<script lang="ts" setup>
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, inject, useCssModule } from 'vue';
  import InlineSvg from 'vue-inline-svg';

  import type { StashProvideState } from '../../../types/misc';
  import type { IconSize } from '../Icon/Icon.types';
  import type { IntegrationIconName } from './IntegrationIcon.types';

  export interface IntegrationIconProps {
    id?: string;

    /**
     * The filename of the icon that should be displayed
     */
    name: IntegrationIconName;

    /**
     * Accessible, short-text description for the icon. Not rendered as part of the graphic, but
     * browsers usually display it as a tooltip and screen readers use this.
     */
    title?: string;

    /**
     * The size of the icon
     * Options: large (32x32px), default (24x24px), dense (20x20px), small (14x14px).
     */
    size?: IconSize;

    /**
     * Icon's custom static path. It'll default to either the staticPath defined on the library installation or '/static' if none are provided.
     */
    staticPath?: string;
  }

  defineOptions({
    name: 'll-icon',
  });

  const props = withDefaults(defineProps<IntegrationIconProps>(), {
    id: () => uniqueId('ll-icon-'),
    size: 'standard',
    title: '',
    staticPath: '',
  });
  const classes = useCssModule();
  const stashOptions = inject<Partial<StashProvideState>>('stashOptions', {
    staticPath: '/static',
  });

  const computedStaticPath = computed(() => {
    return props.staticPath || stashOptions?.staticPath;
  });

  /**
   * Event handler to add the use tag for the specific icon we need to the svg
   */
  const transformSvgSource = (svgElem: SVGElement) => {
    if (!svgElem) {
      return svgElem;
    }

    const useNode = document.createElement('use');
    useNode.setAttribute('href', `#${props.name}`);

    // Grab icon from sprite
    const symbolNode = svgElem.querySelector(`#${props.name}`);

    // This really shouldn't happen but if the spritesheet gets out of sync with the `IconName` type it could
    if (!symbolNode) {
      // still insert <use> element but it will not be found (this is more for tests than anything else)
      svgElem.insertBefore(useNode, svgElem.firstChild);
      return svgElem;
    }

    /**
     * Repeatedly inlining the entire spritesheet was causing failures in mobile Safari, and also
     * performance issues on long list pages where the svg was being inlined over and over.
     * Since the SVG is cached after the first request, this callback is just removing the unnecessary
     * <symbol> nodes to slim down the DOM manipulation and insertion.
     */
    // Replace all children with just the one <symbol> element and <use> element
    svgElem.replaceChildren(symbolNode, useNode);

    return svgElem;
  };
</script>

<!-- Use inline svg so that requests to pull the spritesheet are done via JS and avoid
  browser restrictions for different domains for svgs.  inline svg also caches the requests
  so we don't load the spritesheet multiple times
  Reference: https://oreillymedia.github.io/Using_SVG/extras/ch10-cors.html -->
<template>
  <InlineSvg
    :id="props.id"
    role="presentation"
    :aria-labelledby="props.id"
    class="stash-icon"
    :class="[
      classes.icon,
      `icon--${props.name}`,
      {
        [classes.standard]: props.size === 'standard',
        [classes.dense]: props.size === 'dense',
        [classes.small]: props.size === 'small',
        [classes.large]: props.size === 'large',
      },
    ]"
    data-test="stash-integration-icon"
    :src="`${computedStaticPath}/spritesheet.svg`"
    :title="props.title"
    :transform-source="transformSvgSource"
  />
</template>

<style module>
  .icon {
    display: inline-block;
    fill: currentcolor;
    vertical-align: middle;
  }

  .standard {
    height: 24px;
    min-height: 24px;
    min-width: 24px;
    width: 24px;
  }

  .dense {
    height: 20px;
    min-height: 20px;
    min-width: 20px;
    width: 20px;
  }

  .small {
    height: 14px;
    min-height: 14px;
    min-width: 14px;
    width: 14px;
  }

  .large {
    height: 32px;
    min-height: 32px;
    min-width: 32px;
    width: 32px;
  }
</style>
